import { CloseOutlined } from '@ant-design/icons'
import { Button, Drawer, Typography } from 'antd'
import {
  type CSSProperties,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { cimsStorage } from 'utils/storage'
import { CIMSDrawerProps } from './types'
import styles from './index.module.css'

export { useCIMSDrawer } from './hooks/useCIMSDrawer'
export type { UseCIMSDrawerValues } from './hooks/useCIMSDrawer'
export type { CIMSDrawerProps } from './types'

const DEFAULT_DRAWER_WIDTH_RATIO = 1 / 3
const MIN_LIVE_DRAWER_WIDTH_RATIO = 1 / 10
const MIN_PERSISTED_DRAWER_WIDTH_RATIO = 1 / 4
const MAX_DRAWER_WIDTH_RATIO = 3 / 4
const KEYBOARD_DRAWER_WIDTH_STEP_RATIO = 1 / 20
let nextDrawerTitleId = 0
const DRAWER_CSS_VARIABLES: CSSProperties & {
  '--cims-drawer-peek-min-width': string
} = {
  '--cims-drawer-peek-min-width': `${MIN_PERSISTED_DRAWER_WIDTH_RATIO * 100}vw`,
}

function getDrawerTitleId() {
  nextDrawerTitleId += 1
  return `cims-drawer-title-${nextDrawerTitleId}`
}

/**
 * Clamps a drawer width ratio between a minimum and the maximum supported size.
 *
 * @param widthRatio The drawer width as a ratio of the viewport width.
 * @param minWidthRatio The minimum ratio to enforce.
 */
function clampDrawerWidthRatio(widthRatio: number, minWidthRatio: number) {
  return Math.min(Math.max(widthRatio, minWidthRatio), MAX_DRAWER_WIDTH_RATIO)
}

/**
 * Resolves the width ratio that should be persisted after a drag ends.
 *
 * @param widthRatio The live drawer width as a ratio of the viewport width.
 */
function getPersistedDrawerWidthRatio(widthRatio: number) {
  /**
   * Users can temporarily shrink the drawer to 10% as a peek behavior, but the
   * next drawer session should reopen at a usable minimum of 25%.
   */
  return clampDrawerWidthRatio(widthRatio, MIN_PERSISTED_DRAWER_WIDTH_RATIO)
}

/**
 * Resolves the drawer width ratio from a cursor position.
 *
 * @param clientX The cursor's horizontal viewport position.
 */
function getDrawerWidthRatioFromClientX(clientX: number) {
  if (window.innerWidth <= 0) return DEFAULT_DRAWER_WIDTH_RATIO

  return clampDrawerWidthRatio(
    (window.innerWidth - clientX) / window.innerWidth,
    MIN_LIVE_DRAWER_WIDTH_RATIO
  )
}

/**
 * Resolves the drawer width ratio for keyboard resize commands.
 *
 * @param options Keyboard resize options.
 * @param options.key The key pressed while focused on the resize control.
 * @param options.widthRatio The current drawer width as a ratio of the viewport width.
 */
function getKeyboardDrawerWidthRatio({
  key,
  widthRatio,
}: {
  key: string
  widthRatio: number
}) {
  let nextWidthRatio: number | undefined
  if (key === 'ArrowLeft') {
    nextWidthRatio = widthRatio + KEYBOARD_DRAWER_WIDTH_STEP_RATIO
  } else if (key === 'ArrowRight') {
    nextWidthRatio = widthRatio - KEYBOARD_DRAWER_WIDTH_STEP_RATIO
  } else if (key === 'Home') {
    nextWidthRatio = MIN_LIVE_DRAWER_WIDTH_RATIO
  } else if (key === 'End') {
    nextWidthRatio = MAX_DRAWER_WIDTH_RATIO
  }

  if (nextWidthRatio === undefined) return undefined
  return clampDrawerWidthRatio(nextWidthRatio, MIN_LIVE_DRAWER_WIDTH_RATIO)
}

/**
 * Gets the configured drawer width ratio for a new drawer session.
 */
function getInitialDrawerWidthRatio() {
  const persistedWidthRatio = cimsStorage.drawerWidthRatio
  if (persistedWidthRatio === undefined) return DEFAULT_DRAWER_WIDTH_RATIO

  return getPersistedDrawerWidthRatio(persistedWidthRatio)
}

/**
 * Gets the first touch point's horizontal viewport position.
 *
 * @param event The touch event emitted while resizing the drawer.
 */
function getTouchClientX(event: TouchEvent) {
  return event.touches[0]?.clientX
}

/**
 * Joins optional CSS module class names while avoiding empty class attributes
 * in tests where CSS imports are stubbed.
 *
 * @param classNames Class names to join.
 */
function joinClassNames(...classNames: Array<string | false | undefined>) {
  const className = classNames.filter(Boolean).join(' ')
  return className || undefined
}

/**
 * A standardized right-side drawer with mouse, touch, and keyboard resizing.
 *
 * Drawer width is persisted globally so each drawer opens at the user's last
 * saved width.
 *
 * @example
 * const [open, setOpen] = useState(false)
 *
 * return (
 *  <CIMSDrawer
 *   open={open}
 *   onClose={() => setOpen(false)}
 *   title="Edit Notification"
 *   footer={<CIMSButton onClick={() => setOpen(false)}>Save</CIMSButton>}
 *  >
 *   <NotificationForm />
 *  </CIMSDrawer>
 * )
 */
function CIMSDrawer({
  open,
  onClose,
  title,
  children,
  footer,
  afterOpenChange,
  ...drawerProps
}: CIMSDrawerProps) {
  const [titleId] = useState(getDrawerTitleId)
  const [widthRatio, setWidthRatio] = useState(getInitialDrawerWidthRatio)
  const [resizing, setResizing] = useState(false)
  const isPeeking = widthRatio < MIN_PERSISTED_DRAWER_WIDTH_RATIO

  const resizeToClientX = useCallback((clientX: number) => {
    setWidthRatio(getDrawerWidthRatioFromClientX(clientX))
  }, [])

  /*
   * Ant Design 4 does not forward aria-labelledby to rc-drawer's dialog node,
   * so associate the custom title after the inner shell mounts.
   */
  const labelDrawerDialog = useCallback(
    (drawer: HTMLDivElement | null) => {
      drawer
        ?.closest('[role="dialog"]')
        ?.setAttribute('aria-labelledby', titleId)
    },
    [titleId]
  )

  /*
   * Reset while the drawer is closed so a peeked drawer reopens at the persisted
   * usable width instead of briefly rendering at the stale live width.
   */
  const handleAfterOpenChange = useCallback(
    (open: boolean) => {
      if (!open) setWidthRatio(getInitialDrawerWidthRatio())

      afterOpenChange?.(open)
    },
    [afterOpenChange]
  )

  const stopResizing = useCallback(() => {
    setResizing(false)
    setWidthRatio((widthRatio) => {
      cimsStorage.setDrawerWidthRatio(getPersistedDrawerWidthRatio(widthRatio))
      return widthRatio
    })
  }, [])

  /**
   * Resizes the drawer from keyboard commands on the resize control.
   *
   * @param event The resize control's keyboard event.
   */
  const handleResizeKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const nextWidthRatio = getKeyboardDrawerWidthRatio({
        key: event.key,
        widthRatio,
      })
      if (nextWidthRatio === undefined) return

      event.preventDefault()
      cimsStorage.setDrawerWidthRatio(
        getPersistedDrawerWidthRatio(nextWidthRatio)
      )
      setWidthRatio(nextWidthRatio)
    },
    [widthRatio]
  )

  useEffect(() => {
    if (!resizing) return

    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect
    const touchEventOptions = { capture: false, passive: false }

    /**
     * Updates the drawer width while the user is dragging the resize handle.
     *
     * @param event The document-level mouse move event.
     */
    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault()
      resizeToClientX(event.clientX)
    }

    /**
     * Updates the drawer width while the user is dragging the resize handle on
     * a touch screen.
     *
     * @param event The document-level touch move event.
     */
    const handleTouchMove = (event: TouchEvent) => {
      const clientX = getTouchClientX(event)
      if (clientX === undefined) return

      event.preventDefault()
      resizeToClientX(clientX)
    }

    document.body.style.cursor = 'ew-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResizing)
    document.addEventListener('touchmove', handleTouchMove, touchEventOptions)
    document.addEventListener('touchend', stopResizing)

    return () => {
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResizing)
      document.removeEventListener(
        'touchmove',
        handleTouchMove,
        touchEventOptions
      )
      document.removeEventListener('touchend', stopResizing)
    }
  }, [resizeToClientX, resizing, stopResizing])

  return (
    <Drawer
      {...drawerProps}
      placement="right"
      open={open}
      width={`${widthRatio * 100}vw`}
      afterOpenChange={handleAfterOpenChange}
      onClose={onClose}
      destroyOnClose
      closable={false}
      maskClosable={false}
      bodyStyle={{
        height: '100%',
        padding: 0,
        overflowX: isPeeking ? 'auto' : 'hidden',
        overflowY: 'hidden',
      }}
      contentWrapperStyle={{
        /**
         * Ant Design animates all wrapper changes by default, including width.
         * Disable that transition only during active resize so the drawer stays
         * locked to the cursor.
         */
        transition: resizing ? 'none' : undefined,
      }}
    >
      {/*
       * Ant Design's native header and footer sit in separate drawer slots.
       * This custom shell keeps header, body, footer, and the full-height resize
       * handle in one horizontally scrollable surface for peek mode.
       */}
      <div
        className={joinClassNames(styles.drawer, isPeeking && styles.peek)}
        ref={labelDrawerDialog}
        style={DRAWER_CSS_VARIABLES}
      >
        {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props -- MDN documents aria-value* on focusable, movable separators: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/separator_role. */}
        <div
          aria-label="Resize drawer"
          aria-orientation="vertical"
          aria-valuemax={MAX_DRAWER_WIDTH_RATIO * 100}
          aria-valuemin={MIN_LIVE_DRAWER_WIDTH_RATIO * 100}
          aria-valuenow={Math.round(widthRatio * 100)}
          className={styles.resizeHandle}
          onKeyDown={handleResizeKeyDown}
          onMouseDown={(event) => {
            /*
             * Prevent native selection or drag behavior from competing with the
             * document-level resize handlers.
             */
            event.preventDefault()
            setResizing(true)
            resizeToClientX(event.clientX)
          }}
          onTouchStart={(event) => {
            const clientX = getTouchClientX(event.nativeEvent)
            if (clientX === undefined) return

            event.preventDefault()
            setResizing(true)
            resizeToClientX(clientX)
          }}
          role="separator"
          tabIndex={0}
        />
        <div className={styles.header}>
          <Button
            aria-label="Close drawer"
            icon={<CloseOutlined />}
            onClick={onClose}
            type="text"
          />
          <div className={styles.title}>
            <Typography.Title
              id={titleId}
              level={2}
              style={{ font: 'inherit', margin: 0 }}
            >
              {title}
            </Typography.Title>
          </div>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </Drawer>
  )
}

export default CIMSDrawer
